<template>
  <b-card class="mb-0">
    <b-link class="brand-logo">
      <ok-logo />
    </b-link>

    <error-alert
      v-if="error"
      :error="error"
    />

    <b-card-title class="mb-1">
      {{ $t('auth.mfa-challenge-title') }}
    </b-card-title>
    <b-card-text class="mb-2">
      {{ $t('auth.mfa-challenge-hint') }}
    </b-card-text>

    <validation-observer
      ref="form"
      #default="{invalid}"
    >
      <b-form @submit.prevent="submit">
        <mfa-code-field
          v-model="code"
          :disabled="submitting"
        />

        <b-button
          variant="primary"
          type="submit"
          block
          :disabled="invalid || submitting || accepted"
        >
          {{ $t('auth.mfa-verify') }}
          <b-spinner
            v-if="submitting"
            small
            class="ml-50"
          />
        </b-button>
      </b-form>
    </validation-observer>

    <b-button
      variant="flat-secondary"
      block
      class="mt-1"
      :disabled="submitting"
      @click="$emit('cancel')"
    >
      {{ $t('auth.back-login') }}
    </b-button>
  </b-card>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import {
  BButton, BCard, BCardText, BCardTitle, BForm, BLink, BSpinner,
} from 'bootstrap-vue'
import OkLogo from '@/@okmenu/icons/OkLogo'
import ErrorAlert from '@/@okmenu/components/alerts/ErrorAlert'
import MfaAPI from '@/API/mfa-api'
import { MFA_MAX_ATTEMPTS, isInvalidMfaCodeError, normalizeMfaCode } from '@/utils/mfa-code'
import MfaCodeField from '@/views/auth/mfa/MfaCodeField'

export default {
  name: 'MfaChallengeForm',
  components: {
    MfaCodeField,
    ErrorAlert,
    OkLogo,
    ValidationObserver,
    BButton,
    BCard,
    BCardText,
    BCardTitle,
    BForm,
    BLink,
    BSpinner,
  },
  data() {
    return {
      code: '',
      error: null,
      submitting: false,
      attempts: 0,
      accepted: false,
    }
  },
  computed: {
    mfaToken() {
      return this.$store.getters['auth/mfaToken']
    },
  },
  methods: {
    async submit() {
      this.error = null
      const isValid = await this.$refs.form.validate()
      if (!isValid) return

      const code = normalizeMfaCode(this.code)
      if (!code) {
        this.error = this.$t('auth.mfa-code-format')
        return
      }
      if (!this.mfaToken) {
        this.error = this.$t('auth.mfa-token-missing')
        return
      }
      if (this.accepted) return
      if (this.attempts >= MFA_MAX_ATTEMPTS) {
        this.lockOut()
        return
      }

      this.submitting = true
      try {
        const payload = await MfaAPI.challenge(this.mfaToken, code)
        this.accepted = true
        this.$emit('authenticated', payload)
      } catch (error) {
        if (!isInvalidMfaCodeError(error)) {
          this.error = error
          return
        }
        this.attempts += 1
        const remaining = MFA_MAX_ATTEMPTS - this.attempts
        if (remaining <= 0) {
          this.lockOut()
          return
        }
        this.code = ''
        this.error = `${this.$t('auth.mfa-invalid-code')} ${this.$t('auth.mfa-attempts-left', [remaining])}`
        this.$nextTick(() => {
          if (this.$refs.form) this.$refs.form.reset()
        })
      } finally {
        this.submitting = false
      }
    },
    lockOut() {
      this.$store.dispatch('auth/CLEAR_MFA')
      this.$emit('locked')
    },
  },
}
</script>
